import React, { useState, useEffect } from 'react'
import { makeApiCall } from '../utils/customerUtils'

interface AssociatedCustomer {
  id: number
  name: string
  email?: string
  phone?: string
  association_type: 'created_together' | 'deal_together'
  shared_deals: SharedDeal[]
}

interface SharedDeal {
  id: number
  title: string
  amount: string
  status_name: string
  status_color: string
  role: 'applicant' | 'co_applicant'
  created_at: string
}

interface CustomerOrgChartProps {
  customerId: number
  customerName: string
  onCustomerClick?: (customerId: number) => void
  onDealClick?: (dealId: number) => void
}

export function CustomerOrgChart({ customerId, customerName, onCustomerClick, onDealClick }: CustomerOrgChartProps) {
  const [associatedCustomers, setAssociatedCustomers] = useState<AssociatedCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAssociatedCustomers()
  }, [customerId])

  const fetchAssociatedCustomers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) {
        setError('Authentication required')
        return
      }

      const response = await makeApiCall(`api/customer-associations/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAssociatedCustomers(data.associations || [])
      } else {
        setError('Failed to fetch associated customers')
      }
    } catch (err) {
      console.error('Error fetching associated customers:', err)
      setError('Failed to load customer associations')
    } finally {
      setLoading(false)
    }
  }

  const getAssociationIcon = (type: string) => {
    switch (type) {
      case 'created_together':
        return '👥'
      case 'deal_together':
        return '🤝'
      default:
        return '🔗'
    }
  }

  const getAssociationLabel = (type: string) => {
    switch (type) {
      case 'created_together':
        return 'Created Together'
      case 'deal_together':
        return 'Deal Together'
      default:
        return 'Associated'
    }
  }

  const formatCurrency = (amount: string) => {
    // Handle edge cases where amount might be an object or invalid value
    if (amount === null || amount === undefined || amount === '') {
      return '$0'
    }
    
    // If it's an object, try to extract a numeric value or default to 0
    if (typeof amount === 'object') {
      return '$0'
    }
    
    const numericAmount = parseFloat(amount) || 0
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    )
  }

  if (associatedCustomers.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="mb-4">
          <span className="text-4xl">👤</span>
        </div>
        <p className="text-gray-500 dark:text-gray-400">No associated customers found</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
          This customer has no relationships with other customers
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Central Customer */}
      <div className="flex justify-center">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-xl p-4 shadow-lg max-w-xs text-center">
          <div className="text-2xl mb-2">👤</div>
          <div className="font-semibold text-lg">{customerName}</div>
          <div className="text-blue-100 text-sm">Primary Customer</div>
        </div>
      </div>

      {/* Association Lines and Connected Customers */}
      <div className="relative">
        {/* Vertical Line from Center */}
        <div className="absolute left-1/2 top-0 w-0.5 h-8 bg-gray-300 dark:bg-gray-600 transform -translate-x-0.5"></div>
        
        {/* Horizontal Connector */}
        {associatedCustomers.length > 1 && (
          <div className="absolute top-8 left-1/4 right-1/4 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
        )}

        {/* Associated Customers */}
        <div className={`grid gap-6 mt-12 ${
          associatedCustomers.length === 1 ? 'grid-cols-1 justify-items-center' :
          associatedCustomers.length === 2 ? 'grid-cols-2' :
          associatedCustomers.length === 3 ? 'grid-cols-3' :
          'grid-cols-2 lg:grid-cols-4'
        }`}>
          {associatedCustomers.map((customer, index) => (
            <div key={customer.id} className="relative">
              {/* Connector Line to Center */}
              <div className="absolute bottom-full left-1/2 w-0.5 h-8 bg-gray-300 dark:bg-gray-600 transform -translate-x-0.5"></div>
              
              {/* Customer Card */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                {/* Association Badge */}
                <div className="flex items-center justify-center mb-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    customer.association_type === 'created_together'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                  }`}>
                    <span className="mr-1">{getAssociationIcon(customer.association_type)}</span>
                    {getAssociationLabel(customer.association_type)}
                  </span>
                </div>

                                 {/* Customer Info */}
                 <div className="text-center mb-4">
                   <div 
                     className="font-semibold text-gray-900 dark:text-white mb-1 cursor-pointer hover:text-gray-900 dark:hover:text-gray-600 transition-colors"
                     onClick={() => onCustomerClick?.(customer.id)}
                   >
                     {customer.name}
                   </div>
                  {customer.email && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {customer.email}
                    </div>
                  )}
                  {customer.phone && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {customer.phone}
                    </div>
                  )}
                </div>

                {/* Shared Deals */}
                {customer.shared_deals && customer.shared_deals.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                      Shared Deals ({customer.shared_deals.length})
                    </div>
                    <div className="space-y-2">
                                             {customer.shared_deals.slice(0, 3).map((deal) => (
                         <div 
                           key={deal.id} 
                           className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                           onClick={() => onDealClick?.(deal.id)}
                         >
                           <div className="flex items-center justify-between mb-1">
                             <div className="text-xs font-medium text-gray-900 dark:text-white line-clamp-1 hover:text-gray-900 dark:hover:text-gray-600">
                               {deal.title}
                             </div>
                            <div className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              deal.role === 'applicant'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                            }`}>
                              {deal.role === 'applicant' ? 'App' : 'Co-App'}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                              {formatCurrency(deal.amount)}
                            </div>
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: deal.status_color }}
                            ></div>
                          </div>
                        </div>
                      ))}
                      {customer.shared_deals.length > 3 && (
                        <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                          +{customer.shared_deals.length - 3} more deals
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-600">
              {associatedCustomers.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Associated Customers
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {associatedCustomers.reduce((sum, customer) => sum + (customer.shared_deals?.length || 0), 0)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Shared Deals
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-600">
              {associatedCustomers.filter(c => c.association_type === 'created_together').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Created Together
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 